import { useState, useRef, useEffect } from "react";
import { submitAdmission } from "../../services/admissionService/admissionService";
import { UserCircle, Mail, X, ChevronDown, Phone } from "lucide-react";




// Custom Select Component with flags
const CustomSelect = ({ name, options, value, onChange, placeholder, disabled  }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState("");
    const dropdownRef = useRef(null);

    const toggleDropdown = () => {
        if (!disabled) setIsOpen(!isOpen);
    };

    const handleOptionClick = (option) => {
        if (!disabled) {
            onChange(option, name);
            setIsOpen(false);
            setSearch("");
        }
    };

    useEffect(() => {
        const handleOutsideClick = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleOutsideClick);
        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
        };
    }, []);

    const filteredOptions = options.filter(
        (option) =>
            option.label.toLowerCase().includes(search.toLowerCase()) ||
            option.value.toLowerCase().includes(search.toLowerCase())
    );

    return (
        <div
            className={`relative w-full ${disabled ? "opacity-70 cursor-not-allowed" : ""}`}
            ref={dropdownRef}
        >
            <div
                className="flex items-center justify-between border border-gray-300  p-2 cursor-pointer focus:outline-none focus:ring-2 focus:ring-greenBg transition-all duration-200"
                onClick={toggleDropdown}
            >
                <div className="flex items-center gap-2">
                    {value.country && (
                        <img
                            src={`https://flagcdn.com/w20/${value.country.toLowerCase()}.png`}
                            alt={`${value.label} flag`}
                            className="w-6 h-6 rounded-sm object-contain"
                        />
                    )}
                    <span className="text-gray-700">{value.label || placeholder}</span>
                </div>
                {!disabled && (
                    <ChevronDown
                        className={`w-4 h-4 text-gray-400 transform transition-transform duration-200 ${
                            isOpen ? "rotate-180" : ""
                        }`}
                    />
                )}
            </div>
            {isOpen && !disabled && (
                <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-lg shadow-lg mt-1">
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search..."
                        className="w-full border-b border-gray-200 p-2 text-sm outline-none focus:ring-1 focus:ring-greenBg"
                    />
                    <ul className="max-h-60 overflow-y-auto">
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((option) => (
                                <li
                                    key={option.value}
                                    className="flex items-center gap-2 p-3 hover:bg-gray-100 cursor-pointer transition-colors duration-150"
                                    onClick={() => handleOptionClick(option)}
                                >
                                    <img
                                        src={`https://flagcdn.com/w20/${option.country.toLowerCase()}.png`}
                                        alt={`${option.label} flag`}
                                        className=" rounded-sm object-contain"
                                    />
                                    <span>{option.label}</span>
                                </li>
                            ))
                        ) : (
                            <li className="p-3 text-gray-500 text-sm">No results found</li>
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
};

// Initial state of the form
const initialFormState = {
    firstName: "",
    lastName: "",
    city: "",
    country: { value: "United State", label: "United State", country: "US" },
    state: "",
    phone: "",
    email: "",
    phoneCode: { value: "+1", label: "+1", country: "US" },
    language: [],
    training: [],
    terms: false,
    referBy: "",
    refererInfo: {
        refererName: "",
        phone: "",
    },
};

const LANGUAGE_OPTIONS = ["English", "Punjabi", "Hindi"];
const TRAINING_OPTIONS = [
    "Freight Originator Business Full Training",
    "Truck Dispatcher Training",
    "Freight Broker Training",
    "Enterprise Broker Training",
];

const refererList = ["Google Search", "Instagram", "Facebook", "Friend or Family"];

export default function AdmissionForm({onSuccess = ()=>{} , isInModal = false }) {
    const [formData, setFormData] = useState(initialFormState);
    const [message, setMessage] = useState({ visible: false, text: "", isError: false });

    const countryOptions = [
        { value: "United State", label: "United State", country: "US" },
        { value: "Canada", label: "Canada", country: "CA" },
        { value: "India", label: "India", country: "IN" },
    ];

    const phoneCodeOptions = [
        { value: "+1", label: "+1", country: "US" },
        { value: "+1", label: "+1", country: "CA" },
        { value: "+91", label: "+91", country: "IN" },
    ];

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (type === "checkbox") {
            setFormData((prev) => {
                if (name === "language") {
                    return { ...prev, [name]: checked ? [value] : [] };
                }
                if (name === "training") {
                    return { ...prev, [name]: checked ? [value] : [] };
                }
                return { ...prev, [name]: checked };
            });
        } else {
            // For nested keys like refererInfo.refererName
            if (name.startsWith("refererInfo.")) {
                const key = name.split(".")[1]; // "refererName" or "phone"
                setFormData((prev) => ({
                    ...prev,
                    refererInfo: {
                        ...prev.refererInfo,
                        [key]: value,
                    },
                }));
            } else {
                setFormData((prev) => ({
                    ...prev,
                    [name]: value,
                }));
            }
        }
    };

    const handleSelectChange = (selectedOption, name) => {
        setFormData((prev) => {
            let updated = { ...prev, [name]: selectedOption };
            if (name === "country") {
                const matchedCode = phoneCodeOptions.find(
                    (opt) => opt.country === selectedOption.country
                );
                if (matchedCode) {
                    updated.phoneCode = matchedCode;
                }
            }
            return updated;
        });
    };

    const showMessage = (text, isError = false) => {
        setMessage({ visible: true, text, isError });
        setTimeout(() => {
            setMessage({ visible: false, text: "", isError: false });
        }, 5000);
    };

    const getCourseAmtByCourse = (course) => {
        switch (course) {
            case "Freight Originator Business Full Training":
                return 999;

            default:
                return 799;
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // 🔹 Validations
        if (!formData.firstName.trim() || !formData.lastName.trim()) {
            return showMessage("First & Last Name are required", true);
        }
        if (!formData.city.trim() || !formData.state.trim()) {
            return showMessage("City and State are required", true);
        }
        if (!formData.language.length) {
            return showMessage("Please select a language preference", true);
        }
        if (!formData.training.length) {
            return showMessage("Please select a training option", true);
        }
        if (!/^\d{7,15}$/.test(formData.phone)) {
            return showMessage("Enter a valid phone number (7–15 digits)", true);
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)  &&  !isInModal) {
            return showMessage("Enter a valid email address", true);
        }

        try {
            const response = await submitAdmission({
                ...formData,
                country: formData.country.value,
                phoneCode: formData.phoneCode.value,
                courseAmt: getCourseAmtByCourse(formData.training),
                dueAmt: getCourseAmtByCourse(formData.training),
            });

            if (response?.success || response?.message?.toLowerCase().includes("success")) {
                showMessage(response?.message || "Admission submitted successfully!", false);
                setFormData(initialFormState);
                
                if (!isInModal) {
                    // redirect to zelle
                    window.location.href = "https://awahe.com/zelle";
                }else{

                    if (onSuccess) {
                        onSuccess()
                    }

                }
            } else {
                showMessage(response?.message || "Failed to submit admission!", true);
            }
        } catch (err) {
            console.error("❌ Error:", err);
            showMessage("Server error! Please try again.", true);
        }
    };

    return (
        <div
            className={
                !isInModal
                    ? `flex justify-center items-center bg-gray-100 md:py-3 font-inter h-full 2xl:h-screen`
                    : ""
            }
        >
            <form
                onSubmit={handleSubmit}
                className={`bg-white ${!isInModal ? 'shadow-xl md:p-10' :' md:p-4'}  rounded-2xl  w-full max-w-2xl mx-auto my-auto ${
                    isInModal && "!py-0"
                }`}
            >
                {!isInModal && (
                    <h2 className="text-3xl font-bold text-center mb-8 text-greenBg">
                        Admission Form
                    </h2>
                )}

                {/* Name */}
                <div className="mb-6">
                    <label className="font-semibold text-gray-700 block mb-2">Student Name</label>
                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="flex-1 relative">
                            <div className="absolute inset-y-0 -top-6 left-0 flex items-center pl-3 pointer-events-none">
                                <UserCircle className="h-5 w-5 text-gray-400" />
                            </div>
                            <input
                                type="text"
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleChange}
                                className="w-full border border-gray-300 rounded-lg p-2 pl-10 focus:outline-none focus:ring-2 focus:ring-greenBg transition-all duration-200"
                                required
                            />
                            <p className="text-gray-500 text-sm mt-1">First Name</p>
                        </div>
                        <div className="flex-1">
                            <input
                                type="text"
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleChange}
                                className="w-full border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-greenBg transition-all duration-200"
                                required
                            />
                            <p className="text-gray-500 text-sm mt-1">Last Name</p>
                        </div>
                    </div>
                </div>

                {/* Address */}
                <div className="mb-6">
                    <div className="grid md:grid-cols-2 gap-4">
                        <div>
                            <label className="font-semibold text-gray-700 block mb-2">
                                City <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="city"
                                placeholder="City"
                                value={formData.city}
                                onChange={handleChange}
                                className="w-full border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-greenBg transition-all duration-200"
                                required
                            />
                        </div>
                        <div className="flex-1">
                            <label className="font-semibold text-gray-700 block mb-2">
                                State <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="state"
                                placeholder="State"
                                value={formData.state}
                                onChange={handleChange}
                                className="w-full border border-gray-300 rounded-lg p-2 focus:outline-none focus:ring-2 focus:ring-greenBg transition-all duration-200"
                                required
                            />
                        </div>
                    </div>
                </div>

                {/* Country & Phone */}
                <div className="mb-6">
                    <div className="grid md:grid-cols-2 gap-4">
                        <div>
                            <label className="font-semibold text-gray-700 block mb-2">
                                Country <span className="text-red-500">*</span>
                            </label>
                            <CustomSelect
                                name="country"
                                value={formData.country}
                                onChange={handleSelectChange}
                                options={countryOptions}
                                placeholder="Select a country"
                            />
                        </div>
                        <div>
                            <label className="font-semibold text-gray-700 block mb-2">
                                Phone Number <span className="text-red-500">*</span>
                            </label>
                            <div className="flex border border-gray-300  focus-within:ring-2 focus-within:ring-greenBg transition-all duration-200">
                                <div className="w-[80px] border-r border-gray-300 bg-gray-100">
                                    <CustomSelect
                                        name="phoneCode"
                                        value={formData.phoneCode}
                                        onChange={() => {}}
                                        options={phoneCodeOptions}
                                        disabled={true}
                                    />
                                </div>
                                <input
                                    type="tel"
                                    name="phone"
                                    placeholder="Enter number"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    className="flex-2 p-2 bg-transparent rounded-r-lg focus:outline-none focus:ring-0"
                                    required
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="mb-6">
                    <div className="grid md:grid-cols-2 gap-4">
                        {/* Email */}
                        <div>
                            <label className="font-semibold text-gray-700 block mb-2">
                                Email {!isInModal &&  <span className="text-red-500">*</span>} 
                            </label>
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                    <Mail className="h-5 w-5 text-gray-400" />
                                </div>
                                <input
                                    type="email"
                                    name="email"
                                    placeholder="e.g. example@email.com"
                                    value={formData.email}
                                    onChange={handleChange}
                                    className="w-full border border-gray-300 rounded-lg p-2 pl-10 focus:outline-none focus:ring-2 focus:ring-greenBg transition-all duration-200"
                                    required={!isInModal}
                                />
                            </div>
                        </div>

                        {/* Language */}
                        <div>
                            <label className="font-semibold text-gray-700 block mb-3">
                                Language Preference <span className="text-red-500">*</span>
                            </label>

                            <select
                                name="language"
                                value={formData.language}
                                onChange={handleChange}
                                className="w-full  border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-greenBg focus:border-greenBg"
                            >
                                <option value="" disabled>
                                    Select a language
                                </option>
                                {LANGUAGE_OPTIONS.map((lang) => (
                                    <option key={lang} value={lang}>
                                        {lang}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>
                <div className={`mb-8 ${isInModal && "grid md:grid-cols-2 gap-4"} `}>
                    <div>
                        <label className="font-semibold text-gray-700 block mb-3">
                            Training Choice <span className="text-red-500">*</span>
                        </label>

                        <select
                            name="training"
                            value={formData.training}
                            onChange={handleChange}
                            className="w-full  border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-greenBg focus:border-greenBg"
                        >
                            <option value="" disabled>
                                Select training
                            </option>
                            {TRAINING_OPTIONS.map((t) => (
                                <option key={t} value={t}>
                                    {t}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="font-semibold text-gray-700 block mb-3">
                            Refer By <span className="text-red-500">*</span>
                        </label>

                        <select
                            name="referBy"
                            value={formData.referBy}
                            onChange={handleChange}
                            className="w-full  border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-greenBg focus:border-greenBg"
                        >
                            <option value="" disabled>
                                Select Referer
                            </option>
                            {refererList.map((t) => (
                                <option key={t} value={t}>
                                    {t}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {formData.referBy === "Friend or Family" && (
                    <div className="mb-6">
                        <div className="grid md:grid-cols-2 gap-4">
                            {/* Email */}
                            <div>
                                <label className="font-semibold text-gray-700 block mb-2">
                                    Referer Name <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                        <UserCircle className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        type="text"
                                        name="refererInfo.refererName"
                                        placeholder="Referer Name"
                                        value={formData.refererInfo.refererName}
                                        onChange={handleChange}
                                        className="w-full border border-gray-300 rounded-lg p-2 pl-10 focus:outline-none focus:ring-2 focus:ring-greenBg transition-all duration-200"
                                        required
                                    />
                                </div>
                            </div>

                            {/* Language */}
                            <div>
                                <label className="font-semibold text-gray-700 block mb-2">
                                    Referer Phone <span className="text-red-500">*</span>
                                </label>
                                <div className="relative">
                                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                                        <Phone className="h-5 w-5 text-gray-400" />
                                    </div>
                                    <input
                                        type="text"
                                        name="refererInfo.phone"
                                        placeholder="Phone"
                                        value={formData.refererInfo.phone}
                                        onChange={handleChange}
                                        className="w-full border border-gray-300 rounded-lg p-2 pl-10 focus:outline-none focus:ring-2 focus:ring-greenBg transition-all duration-200"
                                        required
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                {/* Terms */}
                {!isInModal && (
                    <div className="mb-8">
                        <label className="flex items-center gap-2 text-gray-700">
                            <input
                                type="checkbox"
                                name="terms"
                                checked={formData.terms}
                                onChange={handleChange}
                                className="form-checkbox h-5 w-5 text-greenBg  border-gray-300 rounded"
                                required
                            />
                            <span className="text-sm">
                                I accept the Message OPT-IN and the <br className="md:hidden" />
                                <a
                                    target="_blank"
                                    className="text-blue-500 hover:underline "
                                    href="https://awahe.com/terms"
                                >
                                    Terms and Conditions.
                                </a>
                            </span>
                        </label>
                    </div>
                )}

                {/* Submit */}
                <button
                    type="submit"
                    className="w-full bg-greenBg text-white font-bold py-3 px-4 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-all duration-200"
                >
                    Submit
                </button>
            </form>

            {/* Message Box */}
            {message.visible && (
                <div
                    className={`fixed top-4 left-1/2 -translate-x-1/2 py-3 px-6 rounded-lg shadow-lg z-50 transition-all duration-100 ${
                        message.isError ? "bg-red-500 text-white" : "bg-green-600 text-white"
                    }`}
                >
                    <div className="flex items-center gap-2">
                        <span>{message.text}</span>
                        <button
                            onClick={() => setMessage({ ...message, visible: false })}
                            className="ml-2"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
